import React, { useState, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import useRoutes from "../../hooks/useRoutes";
import Header from "../../common/Header";
import Footer from "../../common/Footer";
import Menu from "../../common/Menu";
import useDrawerConfig from "../../hooks/useDrawerConfig";
import { FeatureAccess } from "../../services/AuthService";
import { useDispatch } from "react-redux";
import { setAllowedFeature, setView_Case } from "../../redux/slice/featureAccessSlice";
import { FEATURE_ACCESS_STATUS } from "../../common/Constant";

interface MainDashboardProps {
  accessibleFeatures: string[];
  userid: string | null;
  username: string | null;
  firstname: string | null;
  lastname: string | null;
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface RouteConfig {
  path: string;
  component?: React.ComponentType<any>;
  exact?: boolean;
  name?: string;
  [key: string]: any;
}

function MainDashboard({
  accessibleFeatures,
  userid,
  username,
  firstname,
  lastname,
}: MainDashboardProps) {
  const routes: RouteConfig[] = useRoutes();
  const [authorizeRoute, setAuthorizeRoute] = useState<RouteConfig[]>([]);
  const drawerCong: any = useDrawerConfig();
  const [accessibleFeaturess, setAccessibleFeatures] = useState<string[]>([]);
  const dispatch = useDispatch();

  useEffect(() => {
    setAuthorizeRoute(routes);
  }, [routes]);

  useEffect(() => {
    const fetchFeatureAccess = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          throw new Error("Token is not available. Please log in.");
        }
        const featureAccess = await FeatureAccess(token);
        if (Array.isArray(featureAccess.result)) {
          const features = featureAccess.result.map(
            (f: { featureId: number; featureDesc: string }) => f.featureDesc
          );
          setAccessibleFeatures(features);
          
          const hasViewCaseOnly =
            features?.includes(FEATURE_ACCESS_STATUS.VIEW_CASE) &&
            !features?.includes(FEATURE_ACCESS_STATUS.EDIT_CASE);
          dispatch(setView_Case(hasViewCaseOnly));
          dispatch(setAllowedFeature(new Set(features)));
        } else {
          console.error("Unexpected result format:", featureAccess.result);
          setAccessibleFeatures([]);
        }
      } catch (error) {
        console.error("Error fetching feature access", error);
        setAccessibleFeatures([]);
      }
    };

    fetchFeatureAccess();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div style={layoutStyle}>
        <Header
          userid={userid}
          username={username}
          firstname={firstname}
          lastname={lastname}
        />
        <Menu items={drawerCong} />
        <div style={contentStyle}>
          <Routes>
            {authorizeRoute
              .filter(
                (route) =>
                  route.path === "/map/associated_locations" ||
                  accessibleFeaturess.includes(route.feature)
              )
              .map((route, i) => (
                <Route
                  key={i}
                  path={route.path}
                  element={route.component ? <route.component /> : null}
                />
              ))}
          </Routes>
        </div>
        <Footer />
      </div>
    </QueryClientProvider>
  );
}

// CSS Styles for Layout
const layoutStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh", // Ensures the layout spans the full viewport height
};

const contentStyle: React.CSSProperties = {
  flex: 1, // Makes the content take up all available space between header and footer
  padding: "20px", // Optional: Adds spacing to the content
};

export default MainDashboard;
